import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { getImageUrl } from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Upload, X, ImageIcon, CheckCircle, Save } from 'lucide-react';
import UtilityCheckboxes from '@/components/UtilityCheckboxes';
import PropertyAttributesForm from '@/components/PropertyAttributesForm';
import MapAddressPicker from '@/components/MapAddressPicker';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditPropertyPage() {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mainPhotoPreview, setMainPhotoPreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityMode, setCityMode] = useState('preset'); // 'preset' | 'other'
  const [form, setForm] = useState({
    title: '', description: '', price: '', area: '', property_type: 'apartment',
    operation_type: 'sale', state: '', city: '', neighborhood: '', address: '',
    latitude: 0, longitude: 0,
    bedrooms: 0, bathrooms: 0, main_photo: '', gallery: [],
    water_supply: [], electricity: [], connectivity: [],
    condition_status: '', condition_state: '', floor_level: '', features: [],
    reference: '', instagram_urls: ['', '', '']
  });
  const [originalStatus, setOriginalStatus] = useState('');
  const [wakacode, setWakacode] = useState('');

  const headers = { Authorization: `Bearer ${token}` };
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: typeof e === 'string' ? e : e.target.value }));

  useEffect(() => {
    axios.get(`${API}/locations/states`).then(r => setStates(r.data.states || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.state) { setCities([]); return; }
    axios.get(`${API}/locations/cities`, { params: { state: form.state } })
      .then(r => {
        const list = r.data.cities || [];
        setCities(list);
        // If loaded property has a city not in the taxonomy, switch to "Other" mode
        if (form.city && !list.some(c => c.name === form.city)) {
          setCityMode('other');
        }
      })
      .catch(() => setCities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.state]);

  const onStateChange = (val) => {
    setForm(f => ({ ...f, state: val, city: '' }));
    setCityMode('preset');
  };

  const onCitySelect = (val) => {
    if (val === '__other__') {
      setCityMode('other');
      setForm(f => ({ ...f, city: '' }));
    } else {
      setCityMode('preset');
      setForm(f => ({ ...f, city: val }));
    }
  };

  useEffect(() => {
    axios.get(`${API}/properties/${id}`)
      .then(async res => {
        const p = res.data;
        // If property has city but no state, reverse-lookup it from taxonomy
        let resolvedState = p.state || '';
        let resolvedCity = p.city || '';
        if (resolvedCity && !resolvedState) {
          try {
            const lk = await axios.get(`${API}/locations/lookup`, { params: { city: resolvedCity } });
            if (lk.data?.found) {
              resolvedState = lk.data.state;
              resolvedCity = lk.data.city_name;
            }
          } catch { /* keep existing values */ }
        }
        setForm({
          title: p.title || '', description: p.description || '',
          price: String(p.price || ''), area: String(p.area || ''),
          property_type: p.property_type || 'apartment', operation_type: p.operation_type || 'sale',
          state: resolvedState, city: resolvedCity,
          neighborhood: p.neighborhood || '', address: p.address || '',
          latitude: Number(p.latitude || 0), longitude: Number(p.longitude || 0),
          bedrooms: parseInt(p.bedrooms || 0, 10), bathrooms: parseInt(p.bathrooms || 0, 10),
          main_photo: p.main_photo || '', gallery: p.gallery || [],
          water_supply: p.water_supply || [], electricity: p.electricity || [], connectivity: p.connectivity || [],
          condition_status: p.condition_status || '', condition_state: p.condition_state || '',
          floor_level: p.floor_level || '', features: p.features || [],
          reference: p.reference || '',
          instagram_urls: [...(p.instagram_urls || []), '', '', ''].slice(0, 3)
        });
        setOriginalStatus(p.status);
        setWakacode(p.wakacode || '');
        if (p.main_photo) setMainPhotoPreview(getImageUrl(p.main_photo));
        if (p.gallery?.length) setGalleryPreviews(p.gallery.map((g, i) => getImageUrl(g, i)));
      })
      .catch(() => toast.error('Property not found'))
      .finally(() => setLoading(false));
  }, [id]);

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API}/upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
    return res.data.path;
  };

  const handleMainPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMainPhotoPreview(URL.createObjectURL(file));
    try {
      const path = await uploadImage(file);
      setForm(f => ({ ...f, main_photo: path }));
      toast.success('Photo uploaded');
    } catch { toast.error('Upload failed'); setMainPhotoPreview(getImageUrl(form.main_photo)); }
  };

  const handleGallery = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const preview = URL.createObjectURL(file);
      setGalleryPreviews(p => [...p, preview]);
      try {
        const path = await uploadImage(file);
        setForm(f => ({ ...f, gallery: [...f.gallery, path] }));
      } catch { toast.error('Upload failed'); }
    }
  };

  const removeGalleryImage = (index) => {
    setGalleryPreviews(p => p.filter((_, i) => i !== index));
    setForm(f => ({ ...f, gallery: f.gallery.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price || !form.state || !form.city) {
      toast.error(language === 'es' ? 'Por favor selecciona Estado y Ciudad' : 'Please select State and City'); return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/properties/${id}`, {
        ...form, price: parseFloat(form.price), area: parseFloat(form.area || 0),
        bedrooms: parseInt(form.bedrooms), bathrooms: parseInt(form.bathrooms),
        instagram_urls: (form.instagram_urls || []).map(u => (u || '').trim()).filter(Boolean).slice(0, 3),
      }, { headers });
      toast.success(t('common.save') + ' - ' + (originalStatus === 'approved' ? t('property.pending') : 'OK'));
      navigate(-1);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-4">
      <Skeleton className="h-8 w-48" /><Skeleton className="h-[200px] rounded-xl" /><Skeleton className="h-[300px] rounded-xl" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="edit-property-page">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-primary" data-testid="edit-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">
            {t('common.edit')} {t('admin.properties')}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={`text-xs ${originalStatus === 'approved' ? 'bg-green-100 text-green-700' : originalStatus === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
              {t(`property.${originalStatus}`)}
            </Badge>
            <span className="text-xs text-slate-400">ID: {id.slice(0, 8)}...</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-heading font-semibold text-slate-700">{t('submit.basicInfo')}</h3>
            <div>
              <Label>{t('submit.titleField')} *</Label>
              <Input value={form.title} onChange={set('title')} required className="mt-1 h-11 bg-slate-50" data-testid="edit-title" />
            </div>
            <div>
              <Label>{t('submit.descriptionField')} *</Label>
              <Textarea value={form.description} onChange={set('description')} required rows={4} className="mt-1 bg-slate-50" data-testid="edit-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('submit.priceField')} *</Label>
                <Input type="number" value={form.price} onChange={set('price')} required className="mt-1 h-11 bg-slate-50" data-testid="edit-price" />
              </div>
              <div>
                <Label>{t('submit.areaField')}</Label>
                <Input type="number" value={form.area} onChange={set('area')} className="mt-1 h-11 bg-slate-50" data-testid="edit-area" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('search.propertyType')} *</Label>
                <Select value={form.property_type} onValueChange={set('property_type')}>
                  <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid="edit-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">{t('types.apartment')}</SelectItem>
                    <SelectItem value="house">{t('types.house')}</SelectItem>
                    <SelectItem value="land">{t('types.land')}</SelectItem>
                    <SelectItem value="warehouse">{t('types.warehouse')}</SelectItem>
                    <SelectItem value="office">{t('types.office')}</SelectItem>
                    <SelectItem value="commercial_unit">{t('types.commercial_unit')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('search.operationType')}</Label>
                <Select value={form.operation_type} onValueChange={set('operation_type')}>
                  <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid="edit-operation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">{t('operations.sale')}</SelectItem>
                    <SelectItem value="rent">{t('operations.rent')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Reference (editable) + WakaCode (read-only) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{language === 'es' ? 'Referencia (código de la agencia)' : 'Reference (Agency Code)'}</Label>
                <Input
                  value={form.reference}
                  onChange={set('reference')}
                  placeholder={language === 'es' ? 'Ej. MLS-1234' : 'e.g. MLS-1234'}
                  className="mt-1 h-11 bg-slate-50"
                  data-testid="edit-reference"
                  maxLength={64}
                />
              </div>
              <div>
                <Label>{language === 'es' ? 'WakaCode' : 'WakaCode'}</Label>
                <Input
                  value={wakacode || (language === 'es' ? '— se generará al guardar —' : '— generated on save —')}
                  readOnly
                  disabled
                  className="mt-1 h-11 bg-slate-100 font-mono text-[#0B192C] tracking-wider"
                  data-testid="edit-wakacode"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  {language === 'es' ? 'Código oficial WakaCasa (no editable).' : 'Official WakaCasa code (not editable).'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Property Attributes */}
        {form.property_type !== 'land' && (
          <PropertyAttributesForm form={form} setForm={setForm} idPrefix="edit-attr" />
        )}

        {/* Location */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-heading font-semibold text-slate-700">{t('submit.locationInfo')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{language === 'es' ? 'Estado' : 'State'} *</Label>
                <Select value={form.state} onValueChange={onStateChange}>
                  <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid="edit-state">
                    <SelectValue placeholder={language === 'es' ? 'Selecciona un Estado' : 'Select a State'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {states.map(s => (
                      <SelectItem key={s.key} value={s.key} data-testid={`edit-state-opt-${s.key}`}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('submit.cityField')} *</Label>
                <Select value={cityMode === 'other' ? '__other__' : form.city} onValueChange={onCitySelect} disabled={!form.state}>
                  <SelectTrigger className="mt-1 h-11 bg-slate-50 disabled:opacity-60" data-testid="edit-city">
                    <SelectValue placeholder={
                      !form.state
                        ? (language === 'es' ? 'Primero selecciona Estado' : 'Select state first')
                        : (language === 'es' ? 'Selecciona una Ciudad' : 'Select a City')
                    } />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {cities.map(c => (
                      <SelectItem key={c.key} value={c.name} data-testid={`edit-city-opt-${c.key}`}>{c.name}</SelectItem>
                    ))}
                    <SelectItem value="__other__" data-testid="edit-city-opt-other">{language === 'es' ? 'Otra…' : 'Other…'}</SelectItem>
                  </SelectContent>
                </Select>
                {cityMode === 'other' && (
                  <Input
                    value={form.city}
                    onChange={set('city')}
                    placeholder={language === 'es' ? 'Escribe el nombre de la ciudad' : 'Enter city name'}
                    className="mt-2 h-11 bg-slate-50"
                    data-testid="edit-city-other-input"
                  />
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('submit.neighborhoodField')}</Label>
                <Input value={form.neighborhood} onChange={set('neighborhood')} className="mt-1 h-11 bg-slate-50" data-testid="edit-neighborhood" />
              </div>
              <div>
                <Label>{t('submit.addressField')}</Label>
                <Input value={form.address} onChange={set('address')} className="mt-1 h-11 bg-slate-50" data-testid="edit-address" />
              </div>
            </div>

            {/* Interactive Map Picker */}
            <div className="pt-2">
              <Label className="flex items-center justify-between gap-2 mb-1.5">
                <span>{language === 'es' ? 'Ubicación en el Mapa' : 'Pin on Map'}</span>
                <span className="text-[11px] font-normal text-slate-500">
                  {language === 'es' ? 'Arrastra el pin para mayor precisión' : 'Drag the pin to refine'}
                </span>
              </Label>
              <MapAddressPicker
                latitude={Number(form.latitude || 0)}
                longitude={Number(form.longitude || 0)}
                city={form.city}
                defaultQuery={[
                  form.address,
                  form.neighborhood,
                  form.city,
                  states.find(s => s.key === form.state)?.name || '',
                  'Venezuela',
                ].filter(Boolean).join(', ')}
                onChange={({ lat, lng, address }) => {
                  setForm(f => ({
                    ...f,
                    latitude: lat || 0,
                    longitude: lng || 0,
                    address: (!f.address && address) ? address : f.address,
                  }));
                }}
                testId="edit-map"
              />
            </div>
          </CardContent>
        </Card>

        {/* Utilities & Services */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-heading font-semibold text-slate-700">{language === 'es' ? 'Servicios e Infraestructura' : 'Utilities & Services'}</h3>
            <div>
              <Label className="text-xs text-slate-600">{language === 'es' ? 'Suministro de Agua' : 'Water Supply'}</Label>
              <UtilityCheckboxes category="water_supply" selected={form.water_supply} onChange={v => setForm(f => ({ ...f, water_supply: v }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">{language === 'es' ? 'Electricidad' : 'Electricity'}</Label>
              <UtilityCheckboxes category="electricity" selected={form.electricity} onChange={v => setForm(f => ({ ...f, electricity: v }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">{language === 'es' ? 'Conectividad' : 'Connectivity'}</Label>
              <UtilityCheckboxes category="connectivity" selected={form.connectivity} onChange={v => setForm(f => ({ ...f, connectivity: v }))} />
            </div>
          </CardContent>
        </Card>

        {/* Photos */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-heading font-semibold text-slate-700">{t('submit.mediaInfo')}</h3>
            <div>
              <Label>{t('submit.mainPhoto')}</Label>
              <div className="mt-2">
                {mainPhotoPreview ? (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden">
                    <img src={mainPhotoPreview} alt="Main" className="w-full h-full object-cover" />
                    <label className="absolute top-2 right-2 cursor-pointer w-7 h-7 rounded-full bg-white/90 flex items-center justify-center hover:bg-white">
                      <Upload className="w-3.5 h-3.5 text-slate-600" />
                      <input type="file" accept="image/*" onChange={handleMainPhoto} className="hidden" />
                    </label>
                    {form.main_photo && <CheckCircle className="absolute bottom-2 right-2 w-5 h-5 text-green-500" />}
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-primary/50 transition-colors" data-testid="edit-upload-main">
                    <Upload className="w-6 h-6 text-slate-400 mb-1" /><span className="text-sm text-slate-500">{t('submit.uploadPhoto')}</span>
                    <input type="file" accept="image/*" onChange={handleMainPhoto} className="hidden" />
                  </label>
                )}
              </div>
            </div>
            <div>
              <Label>{t('submit.galleryPhotos')}</Label>
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-3">
                {galleryPreviews.map((preview, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={preview} alt={`Gallery ${i}`} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeGalleryImage(i)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="aspect-square flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-primary/50" data-testid="edit-upload-gallery">
                  <ImageIcon className="w-5 h-5 text-slate-400" /><span className="text-xs text-slate-500 mt-1">Add</span>
                  <input type="file" accept="image/*" multiple onChange={handleGallery} className="hidden" />
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instagram URLs (optional, max 3) */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className="text-base font-heading text-[#0B192C]">
                {language === 'es' ? 'Instagram (opcional)' : 'Instagram (optional)'}
              </Label>
              <p className="text-xs text-slate-500 mt-0.5">
                {language === 'es'
                  ? 'Pega hasta 3 URLs de tus posts o reels para mostrarlos en la propiedad.'
                  : 'Paste up to 3 URLs of your posts or reels to show on this listing.'}
              </p>
            </div>
            <div className="space-y-2" data-testid="edit-instagram-urls">
              {[0, 1, 2].map((i) => (
                <input
                  key={i}
                  type="url"
                  placeholder="https://www.instagram.com/p/ABC123/"
                  value={form.instagram_urls?.[i] || ''}
                  onChange={(e) => {
                    const next = [...(form.instagram_urls || ['', '', ''])];
                    next[i] = e.target.value;
                    setForm((f) => ({ ...f, instagram_urls: next }));
                  }}
                  className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
                  data-testid={`edit-instagram-url-${i}`}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        <Button type="submit" disabled={saving} className="w-full h-12 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] rounded-xl font-semibold text-base" data-testid="save-property-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? t('common.loading') : t('common.save')}
        </Button>
      </form>
    </div>
  );
}
